import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationStart } from '@angular/router';
import { concat, Observable, of, throwError, timer, interval } from 'rxjs';
import { delay, ignoreElements, map, switchMap, take } from 'rxjs/operators';
import {
  AppService, Profile, HumanReviewQueue, UploadResult, UploadFileInfo,
  ImageFolderList, ImageSummaryData, ImageGifList, StreamStatus,
  ValidationRow, ReviewVariableData, ExecutionPreviewData,
  TransformChatMessage, TransformSuggestion,
  TargetReloadData, TransformReloadData, PipelineStats, ValidationSummary,
  PagedResult, AuditVariable, ProfileVariableRow, Session, PlanQueryOpts,
} from './app.service';

@Injectable()
export class DemoService extends AppService {
  private readonly http = inject(HttpClient);

  override setRunName(_name: string): void {}

  private readonly DEMO_DELAY = 700;
  /** Multiply all SSE stream intervals by this value. 1 = fast demo, 3 = 3× slower, etc. */
  private readonly SSE_MULT = 1;
  private _forceAllNotMapped = false; // ← change to true to test the all-not-mapped blocking guard

  private _simulateAll = false; // ← change to true to fail all once

  private readonly _failedOnce = new Set<string>();
  protected failOnce(key: string): boolean {
    if (this._failedOnce.has(key)) return false;
    this._failedOnce.add(key);
    return true;
  }
  protected shouldFail(key: string): boolean {
    return this._simulateAll && this.failOnce(key);
  }

  constructor() {
    super();
    // Reset simulation state on every navigation so the first call per page always fails
    inject(Router).events.subscribe(e => {
      if (e instanceof NavigationStart) this._failedOnce.clear();
    });
  }

  // ── Upload Module ─────────────────────────────────────────

  uploadFile(file: File, card: string, slot: number): Promise<UploadResult> {
    console.log(`[Demo] File uploaded: "${file.name}" (card: ${card}, slot: ${slot})`);
    if (this.shouldFail('uploadFile')) return Promise.reject({ message: 'Simulated upload error' });
    // if (this.failOnce('uploadFile')) return Promise.reject({ message: 'Simulated upload error' }); // ← uncomment to test individually
    return Promise.resolve({ ok: true, savedName: file.name });
  }

  listUploadFiles(): Observable<UploadFileInfo[]> {
    if (this.shouldFail('listUploadFiles')) return throwError(() => ({ message: 'Simulated error: could not load file list' }));
    // if (this.failOnce('listUploadFiles')) return throwError(() => ({ message: 'Simulated error: could not load file list' })); // ← uncomment to test individually
    return this.http.get<{ files: UploadFileInfo[] }>('assets/demo/upload_files.json')
      .pipe(delay(this.DEMO_DELAY), map(res => res.files));
  }

  getUploadFilePreviewUrl(name: string): string {
    const filename = name.split('/').pop()!;
    return `assets/demo/upload/${encodeURIComponent(filename)}`;
  }

  getPreviewLoadDelay(): number { return 1200; }
  override getPdfPreviewShouldFail(): boolean { return this.shouldFail('pdfPreview'); }

  watchUploadStatus(): Observable<StreamStatus> {
    if (this.shouldFail('watchUploadStatus')) return throwError(() => ({ message: 'Simulated error: profiling start failed' }));
    // if (this.failOnce('watchUploadStatus')) return throwError(() => ({ message: 'Simulated error: profiling start failed' })); // ← uncomment to test individually
    const messages = [
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.',
      'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit.',
    ];
    return concat(
      interval(1000 * this.SSE_MULT).pipe(take(messages.length), map(i => ({ message: messages[i] }))),
      timer(800 * this.SSE_MULT).pipe(ignoreElements())
    );
  }

  reconnectUploadStatus(): Observable<StreamStatus> { return this.watchUploadStatus(); }

  getProfiles(): Observable<Record<string, Profile>> {
    if (this.shouldFail('dataProfilingData')) return throwError(() => ({ message: 'Simulated error: could not load profiling data' }));
    // if (this.failOnce('getProfiles')) return throwError(() => ({ message: 'Simulated error: could not load profiling data' })); // ← uncomment to test individually
    return this.http.get<Record<string, Profile>>('assets/demo/profiles_metadata.json').pipe(
      delay(this.DEMO_DELAY),
    );
  }

  watchProfilingStatus(): Observable<StreamStatus> {
    if (this.shouldFail('watchProfilingStatus')) return throwError(() => ({ message: 'Simulated error: profiling pipeline failed' }));
    // if (this.failOnce('watchProfilingStatus')) return throwError(() => ({ message: 'Simulated error: profiling pipeline failed' })); // ← uncomment to test individually
    const messages = [
      'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta.',
      'Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt neque porro quisquam est.',
    ];
    return concat(
      interval(1000 * this.SSE_MULT).pipe(take(messages.length), map(i => ({ message: messages[i] }))),
      timer(800 * this.SSE_MULT).pipe(ignoreElements())
    );
  }

  reconnectProfilingStatus(): Observable<StreamStatus> { return this.watchProfilingStatus(); }

  watchPlanningStatus(): Observable<StreamStatus> {
    if (this.shouldFail('watchPlanningStatus')) return throwError(() => ({ message: 'Simulated error: planning pipeline failed' }));
    // if (this.failOnce('watchPlanningStatus')) return throwError(() => ({ message: 'Simulated error: planning pipeline failed' })); // ← uncomment to test individually
    const messages = [
      'At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident.',
      'Nam libero tempore cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus, omnis voluptas assumenda est, omnis dolor repellendus itaque earum rerum.',
    ];
    return concat(
      interval(1000 * this.SSE_MULT).pipe(take(messages.length), map(i => ({ message: messages[i] }))),
      timer(800 * this.SSE_MULT).pipe(ignoreElements())
    );
  }

  reconnectPlanningStatus(): Observable<StreamStatus> { return this.watchPlanningStatus(); }

  // ── Strategy & Planning Module ────────────────────────────

  getHumanReviewQueue(threshold: number | null): Observable<HumanReviewQueue> {
    if (this.shouldFail('reviewPageLoad')) return throwError(() => ({ message: 'Simulated error: could not load review queue' }));
    type Row = { id: string; decision_confidence: number; target_confidence: number; transform_confidence: number };
    return this.http.get<{ variables: Row[] }>('assets/demo/execution_plan_confidences.json').pipe(
      delay(300),
      map(data => {
        const ids = data.variables
          .filter(v => { const avg = (v.decision_confidence + v.target_confidence + v.transform_confidence) / 3; return threshold === null || (avg <= threshold && avg !== 1); })
          .map(v => v.id);
        return { ids, total: ids.length };
      }),
    );
  }

  checkAllNotMapped(): Observable<{ all_not_mapped: boolean }> {
    if (this.shouldFail('checkAllNotMapped')) return throwError(() => ({ message: 'Simulated error: could not check variable mapping status' }));
    return of({ all_not_mapped: this._forceAllNotMapped }).pipe(delay(this.DEMO_DELAY));
  }

  saveSteps(variableId: string, steps: unknown[]): void {
    console.log(`[Demo] saveSteps — variable: "${variableId}"`, steps);
  }

  getReviewData(decisionId: string): Observable<ReviewVariableData> {
    if (this.shouldFail('reviewPageLoad')) return throwError(() => ({ message: 'Simulated error: could not load review data' }));
    return this.http.get<ReviewVariableData>(`assets/demo/review/${decisionId}.json`).pipe(delay(this.DEMO_DELAY));
  }

  startDecisionChange(_decisionId: string, _decision: string): Observable<{ ok: boolean }> {
    if (this.shouldFail('decisionChangeFlow')) return throwError(() => ({ message: 'Simulated error: decision change request failed' }));
    // if (this.failOnce('startDecisionChange')) return throwError(() => ({ message: 'Simulated error: decision change request failed' })); // ← uncomment to test individually
    return of({ ok: true });
  }

  getDecisionTargets(_decisionId: string, _decision: string): Observable<TargetReloadData> {
    if (this.shouldFail('decisionChangeFlow')) return throwError(() => ({ message: 'Simulated error: could not load decision targets' }));
    // if (this.failOnce('getDecisionTargets')) return throwError(() => ({ message: 'Simulated error: could not load decision targets' })); // ← uncomment to test individually
    return this.http.get<TargetReloadData>('assets/demo/review/decision_reload.json');
  }

  startTransformReload(_decisionId: string, _decision: string, _targetId: string): Observable<{ ok: boolean }> {
    if (this.shouldFail('transformReloadFlow')) return throwError(() => ({ message: 'Simulated error: transform reload request failed' }));
    // if (this.failOnce('startTransformReload')) return throwError(() => ({ message: 'Simulated error: transform reload request failed' })); // ← uncomment to test individually
    return of({ ok: true });
  }

  getTargetTransforms(_decisionId: string, _targetId: string): Observable<TransformReloadData> {
    if (this.shouldFail('transformReloadFlow')) return throwError(() => ({ message: 'Simulated error: could not load transforms' }));
    // if (this.failOnce('getTargetTransforms')) return throwError(() => ({ message: 'Simulated error: could not load transforms' })); // ← uncomment to test individually
    return this.http.get<TransformReloadData>('assets/demo/review/transform_reload.json');
  }

  private readonly reviewedKeys = new Set<string>();

  markVariableReviewed(
    decisionId: string,
    _decision: string,
    _targetId: string | null,
    _selectedTransformation: unknown,
  ): Observable<void> {
    this.reviewedKeys.add(decisionId);
    return of(undefined);
  }

  splitVariable(_decisionId: string): Observable<{ created_variable_ids: string[] }> {
    return of({ created_variable_ids: [] });
  }

  suggestTransformation(
    _decisionId: string,
    _decision: string,
    _targetId: string,
    _selectedTransformation: unknown,
    _userMessage: string,
    _history: TransformChatMessage[],
  ): Observable<TransformSuggestion> {
    if (this.shouldFail('suggestTransformation')) return throwError(() => ({ message: 'Simulated error: chat pipeline failed' }));
    return timer(500).pipe(
      switchMap(() => this.http.get<TransformSuggestion>('assets/demo/transform_suggestions.json'))
    );
  }

  // ── Execution Module ──────────────────────────────────────

  startExecution(): Observable<{ ok: boolean; session_id: string }> {
    return of({ ok: true, session_id: 'demo-session' });
  }

  watchExecutionStatus(): Observable<StreamStatus> {
    if (this.shouldFail('watchExecutionStatus')) return throwError(() => ({ message: 'Simulated error: execution pipeline failed' }));
    const messages = [
      'Temporibus autem quibusdam et aut officiis debitis rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint molestiae non recusandae itaque earum rerum hic tenetur a sapiente delectus.',
      'Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur vel illum qui dolorem eum fugiat quo voluptas nulla pariatur.',
    ];
    return concat(
      interval(1000 * this.SSE_MULT).pipe(take(messages.length), map(i => ({ message: messages[i] }))),
      timer(800 * this.SSE_MULT).pipe(ignoreElements())
    );
  }

  reconnectExecutionStatus(): Observable<StreamStatus> { return this.watchExecutionStatus(); }

  watchDecisionChangeStatus(_decisionId: string, _decision: string): Observable<StreamStatus> {
    const messages = [
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua ut enim ad minim veniam quis nostrud exercitation.',
      'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident deserunt mollit anim.',
      'Ut labore et dolore magnam aliquam quaerat voluptatem nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit sed quia consequuntur magni dolores eos.',
    ];
    return concat(
      interval(900 * this.SSE_MULT).pipe(take(messages.length), map(i => ({ message: messages[i] }))),
      timer(600 * this.SSE_MULT).pipe(ignoreElements())
    );
  }

  watchTransformReloadStatus(_decisionId: string, _decision: string, _targetId: string): Observable<StreamStatus> {
    const messages = [
      'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium totam rem aperiam eaque ipsa quae ab illo inventore veritatis et quasi architecto.',
      'At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati.',
      'Nam libero tempore cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus omnis voluptas assumenda est omnis dolor repellendus.',
    ];
    return concat(
      interval(900 * this.SSE_MULT).pipe(take(messages.length), map(i => ({ message: messages[i] }))),
      timer(600 * this.SSE_MULT).pipe(ignoreElements())
    );
  }

  searchValidationResults(q: string, page: number, pageSize: number, _sortBy?: string): Observable<PagedResult<ValidationRow>> {
    if (this.shouldFail('getValidationResults')) return throwError(() => ({ message: 'Simulated error: could not load validation results' }));
    const term = q.toLowerCase();
    return this.http.get<PagedResult<ValidationRow>>(`assets/demo/validation/ps${pageSize}/p0.json`).pipe(
      delay(this.DEMO_DELAY),
      map((result: PagedResult<ValidationRow>) => {
        const filtered = result.data.filter(v =>
          String(v.variable ?? '').toLowerCase().includes(term) ||
          String(v.concept ?? '').toLowerCase().includes(term)
        );
        const start = page * pageSize;
        return { data: filtered.slice(start, start + pageSize), total: filtered.length, page, page_size: pageSize };
      }),
    );
  }

  searchProfileVariables(dataset: string, q: string, page: number, pageSize: number, _sortBy?: string): Observable<PagedResult<ProfileVariableRow>> {
    if (this.shouldFail('dataProfilingData')) return throwError(() => ({ message: 'Simulated error: could not load profiling variables' }));
    const term = q.toLowerCase();
    return this.http.get<PagedResult<ProfileVariableRow>>(`assets/demo/profile_variables/${dataset}/ps${pageSize}/p0.json`).pipe(
      delay(this.DEMO_DELAY),
      map((result: PagedResult<ProfileVariableRow>) => {
        const filtered = result.data.filter(v =>
          String(v.variable_name ?? '').toLowerCase().includes(term) ||
          String(v.description ?? '').toLowerCase().includes(term) ||
          String(v.data_type ?? '').toLowerCase().includes(term)
        );
        const start = page * pageSize;
        return { data: filtered.slice(start, start + pageSize), total: filtered.length, page, page_size: pageSize };
      }),
    );
  }

  getValidationResults(page: number, pageSize: number, _sortBy?: string): Observable<PagedResult<ValidationRow>> {
    if (this.shouldFail('getValidationResults')) return throwError(() => ({ message: 'Simulated error: could not load validation results' }));
    // if (this.failOnce('getValidationResults')) return throwError(() => ({ message: 'Simulated error: could not load validation results' })); // ← uncomment to test individually
    return this.http.get<PagedResult<ValidationRow>>(`assets/demo/validation/ps${pageSize}/p${page}.json`).pipe(
      delay(this.DEMO_DELAY),
    );
  }

  getValidationSummary(): Observable<ValidationSummary> {
    if (this.shouldFail('getValidationSummary')) return throwError(() => ({ message: 'Simulated error: could not load validation summary' }));
    return this.http.get<ValidationSummary>('assets/demo/validation_summary.json').pipe(delay(this.DEMO_DELAY));
  }

  notifyReplan(_failedVariables: string[]): Observable<void> {
    return of(undefined);
  }

  getPipelineStats(): Observable<PipelineStats> {
    if (this.shouldFail('getPipelineStats')) return throwError(() => ({ message: 'Simulated error: could not load pipeline stats' }));
    return this.http.get<PipelineStats>('assets/demo/pipeline_stats.json').pipe(delay(this.DEMO_DELAY));
  }

  getExecutionPreview(page: number, pageSize: number, _colLimit?: number): Observable<ExecutionPreviewData> {
    if (this.shouldFail('getExecutionPreview')) return throwError(() => ({ message: 'Simulated error: could not load execution preview' }));
    // if (this.failOnce('getExecutionPreview')) return throwError(() => ({ message: 'Simulated error: could not load execution preview' })); // ← uncomment to test individually
    return this.http.get<ExecutionPreviewData>(`assets/demo/execution_preview/ps${pageSize}/p${page}.json`).pipe(
      delay(this.DEMO_DELAY),
    );
  }

  private applyPlanFilter(data: AuditVariable[], opts?: PlanQueryOpts): AuditVariable[] {
    let result = data;

    if (opts?.replanFilter) result = result.filter(v => v.decision === 'not_mapped');

    const filters = opts?.filters ?? [];

    const decisionFilters = filters.filter(f =>
      ['matched', 'mapped', 'partially_mapped', 'calculated_variable', 'not_mapped'].includes(f)
    );
    if (decisionFilters.length) result = result.filter(v => decisionFilters.includes(v.decision));

    const hiFilter = filters.find(f => f === 'intervention_needed' || f === 'intervention_not_needed');
    if (hiFilter && opts?.threshold !== undefined) {
      const t = opts.threshold;
      result = result.filter(v => {
        const vals = [v.decision_confidence, v.target_confidence, v.transform_confidence]
          .filter((x): x is number => x != null);
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        const needed = avg <= t && avg !== 1;
        return hiFilter === 'intervention_needed' ? needed : !needed;
      });
    }

    return result;
  }

  private applySortToVariables(data: AuditVariable[], sortBy?: string): AuditVariable[] {
    if (!sortBy) return data;
    const avg = (v: AuditVariable): number => {
      const vals = [v.decision_confidence, v.target_confidence, v.transform_confidence]
        .filter((x): x is number => x != null);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    };
    const sorted = [...data];
    switch (sortBy) {
      case 'confidence_desc': return sorted.sort((a, b) => avg(b) - avg(a));
      case 'confidence_asc':  return sorted.sort((a, b) => avg(a) - avg(b));
      case 'reviewed_first':  return sorted.sort((a, b) => (b.reviewed ? 1 : 0) - (a.reviewed ? 1 : 0));
      case 'reviewed_last':   return sorted.sort((a, b) => (a.reviewed ? 1 : 0) - (b.reviewed ? 1 : 0));
      default: return data;
    }
  }

  getPlanVariables(page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>> {
    if (this.shouldFail('getPlanVariables')) return throwError(() => ({ message: 'Simulated error: could not load plan data' }));
    return this.http.get<PagedResult<AuditVariable>>(`assets/demo/plan_variables/ps${pageSize}/p${page}.json`).pipe(
      delay(this.DEMO_DELAY),
      map((result: PagedResult<AuditVariable>) => {
        const data = result.data.map(v => this.reviewedKeys.has(v.id) ? { ...v, reviewed: true } : v);
        const filtered = this.applyPlanFilter(data, opts);
        const sorted = this.applySortToVariables(filtered, opts?.sortBy);
        return { ...result, data: sorted, total: sorted.length };
      }),
    );
  }

  searchPlanVariables(q: string, page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>> {
    if (this.shouldFail('getPlanVariables')) return throwError(() => ({ message: 'Simulated error: could not load plan data' }));
    const term = q.toLowerCase();
    return this.http.get<PagedResult<AuditVariable>>(`assets/demo/plan_variables/ps${pageSize}/p0.json`).pipe(
      delay(this.DEMO_DELAY),
      map((result: PagedResult<AuditVariable>) => {
        let all = result.data.map(v => this.reviewedKeys.has(v.id) ? { ...v, reviewed: true } : v);
        all = this.applyPlanFilter(all, opts);
        const filtered = all.filter(v =>
          String(v.mapping_from ?? '').toLowerCase().includes(term) ||
          String(v.mapping_to ?? '').toLowerCase().includes(term) ||
          String(v.decision ?? '').toLowerCase().replace(/_/g, ' ').includes(term)
        );
        const sorted = this.applySortToVariables(filtered, opts?.sortBy);
        const start = page * pageSize;
        return { data: sorted.slice(start, start + pageSize), total: sorted.length, page, page_size: pageSize };
      }),
    );
  }

  getProfileVariables(dataset: string, page: number, pageSize: number, _sortBy?: string): Observable<PagedResult<ProfileVariableRow>> {
    if (this.shouldFail('dataProfilingData')) return throwError(() => ({ message: 'Simulated error: could not load profiling variables' }));
    return this.http.get<PagedResult<ProfileVariableRow>>(`assets/demo/profile_variables/${dataset}/ps${pageSize}/p${page}.json`).pipe(
      delay(this.DEMO_DELAY),
    );
  }

  // ── Images Module ─────────────────────────────────────────

  listImageFolders(path: string): Observable<ImageFolderList> {
    if (this.shouldFail('listImageFolders')) return throwError(() => ({ message: 'Simulated error: could not load folders' }));
    // if (this.failOnce('listImageFolders')) return throwError(() => ({ message: 'Simulated error: could not load folders' })); // ← uncomment to test individually
    if (path === '') return of({ directories: ['MRIs_Participants_demo'] }).pipe(delay(this.DEMO_DELAY));
    return of({ directories: [] }).pipe(delay(this.DEMO_DELAY));
  }

  getImageSummary(_folder: string): Observable<ImageSummaryData> {
    if (this.shouldFail('getImageSummary')) return throwError(() => ({ message: 'Simulated error: could not load image summary' }));
    // if (this.failOnce('getImageSummary')) return throwError(() => ({ message: 'Simulated error: could not load image summary' })); // ← uncomment to test individually
    return this.http.get<ImageSummaryData>('assets/demo/images_summary.json').pipe(delay(this.DEMO_DELAY));
  }

  listImageGifs(_folder: string, page: number, pageSize: number): Observable<ImageGifList> {
    if (this.shouldFail('listImageGifs')) return throwError(() => ({ message: 'Simulated error: could not load GIF list' }));
    // if (this.failOnce('listImageGifs')) return throwError(() => ({ message: 'Simulated error: could not load GIF list' })); // ← uncomment to test individually
    return this.http.get<ImageGifList>(`assets/demo/gifs/ps${pageSize}/p${page}.json`).pipe(
      delay(this.DEMO_DELAY),
    );
  }

  runPreprocessing(_folder: string): Observable<void> {
    return timer(6000).pipe(map(() => { }));
  }

  watchPreprocessingStatus(_folder: string): Observable<StreamStatus> {
    const messages = [
      '[DEMO] Identifying MRI images...',
      '[DEMO] Running skull stripping...',
      '[DEMO] Reorienting images...',
      '[DEMO] Registering images...',
      '[DEMO] Generating GIF previews...',
    ];
    return interval(1000).pipe(take(messages.length), map(i => ({ message: messages[i] })));
  }

  getGifUrl(path: string): string {
    return `assets/demo/Images/${path}`;
  }

  // ── Session history ───────────────────────────────────────

  getSessions(): Observable<Session[]> {
    if (this.shouldFail('getSessions')) return throwError(() => ({ message: 'Simulated error: could not load run history' }));
    return this.http.get<{ sessions: Session[] }>('assets/demo/sessions.json').pipe(
      delay(this.DEMO_DELAY),
      map(res => res.sessions),
    );
  }

  renameSession(_sessionId: string, _name: string): Observable<{ ok: boolean }> {
    return of({ ok: true });
  }

  deleteSession(_sessionId: string): Observable<void> {
    if (this.shouldFail('deleteSession')) return throwError(() => ({ message: 'Simulated error: could not delete session' }));
    return of(undefined).pipe(delay(this.DEMO_DELAY));
  }

  finalizeSession(): Observable<{ ok: boolean }> {
    return of({ ok: true }).pipe(delay(this.DEMO_DELAY));
  }
}
